import { useEffect, useRef, useState } from "react";
import type { ChoreInstance } from "../lib/api";

/* The week's jobs as a grid: one row per job, one column per day.
 *
 * This is the progress bar opened out. The bar answers "am I going to get
 * paid"; the grid answers "which days did I actually do it", which is the
 * question that comes next and the one a parent needs when they are squaring
 * the week up on Sunday. Same drawing both sides of the app -- a child who
 * learns to read their own row can read the one a grown-up is correcting.
 *
 * Days come from the server, in the PERIOD's timezone. Deriving them from the
 * tablet's clock would put the whole grid one column out for any family whose
 * device disagrees with the family timezone.
 */

const WEEKDAY = ["Su", "M", "T", "W", "Th", "F", "Sa"];

export type CellState = "done" | "claimed" | "missed" | "excused" | "open";

/* What a grown-up can say about a square.
 *
 * "Missed" is deliberately absent: it is not a thing a parent asserts, it is
 * what close_period_instances concludes when the week ends on an untouched
 * job. Offering it here would let a parent freeze that judgement days before
 * the week is over, and it would mean the same square could be reached two
 * ways with different meanings. "Not done" puts the job back to untouched and
 * lets the week decide.
 */
export type SetState = "done" | "not_done" | "excused";

const CHOICES: { state: SetState; label: string; hint: string }[] = [
  { state: "done", label: "Done", hint: "Counts toward the week" },
  { state: "not_done", label: "Not done", hint: "Back to untouched" },
  { state: "excused", label: "Excused", hint: "Not expected \u2014 does not count against them" },
];

export function stateOf(instance: ChoreInstance): CellState {
  switch (instance.status) {
    case "approved":
      return "done";
    case "claimed":
      return "claimed";
    case "missed":
      return "missed";
    case "excused":
      return "excused";
    default:
      return "open";
  }
}

/* A local calendar date, parsed as local.
 *
 * new Date("2026-08-28") is parsed as UTC and comes back as the 27th anywhere
 * west of Greenwich, which would mislabel every column. Splitting the parts
 * and using the local constructor is the only reading that cannot drift.
 */
export function localDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function dayLabel(iso: string): string {
  return WEEKDAY[localDate(iso).getDay()];
}

/** One job's name and the instances it has across the period. */
export type GridRow = { name: string; icon: string; byDay: Map<string, ChoreInstance[]> };

export function toRows(instances: ChoreInstance[]): GridRow[] {
  const rows = new Map<string, GridRow>();
  for (const i of instances) {
    // times_per_period appends "#n" to keep the key unique; the column is
    // still the calendar day.
    const day = i.dueLocalDate.split("#")[0];
    const row = rows.get(i.name) ?? { name: i.name, icon: i.icon, byDay: new Map() };
    row.byDay.set(day, [...(row.byDay.get(day) ?? []), i]);
    rows.set(i.name, row);
  }
  return [...rows.values()];
}

export function ChoreGrid({
  instances,
  days,
  todayLocalDate,
  onSet,
  busyId,
  nameWidth = 92,
}: {
  instances: ChoreInstance[];
  days: string[];
  todayLocalDate: string;
  /* Absent on the child's side: there it is a record, not a control. */
  onSet?: (instance: ChoreInstance, next: SetState) => void;
  busyId?: string | null;
  nameWidth?: number;
}) {
  /* Which square has its chooser open. Held here rather than in the cell so
     opening one closes the last -- two menus over a grid this tight would sit
     on top of each other. */
  const [openId, setOpenId] = useState<string | null>(null);

  const rows = toRows(instances);
  if (rows.length === 0 || days.length === 0) return null;

  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          tableLayout: "fixed",
          minWidth: nameWidth + days.length * 34,
        }}
      >
        <thead>
          <tr>
            <th style={{ width: nameWidth }} />
            {days.map((d) => {
              const isToday = d === todayLocalDate;
              return (
                <th
                  key={d}
                  scope="col"
                  style={{
                    fontFamily: "var(--font-num)",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: isToday ? "var(--ink)" : "var(--ink-3)",
                    paddingBottom: 6,
                  }}
                >
                  {dayLabel(d)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name}>
              <th
                scope="row"
                style={{
                  textAlign: "left",
                  fontWeight: 500,
                  fontSize: 13,
                  color: "var(--ink-2)",
                  paddingRight: 8,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.name}
              </th>
              {days.map((d) => (
                <td key={d} style={{ textAlign: "center", padding: "3px 0" }}>
                  <div style={{ display: "grid", gap: 3, justifyItems: "center" }}>
                    {(row.byDay.get(d) ?? [null]).map((instance, n) =>
                      instance ? (
                        <Cell
                          key={instance.id}
                          instance={instance}
                          isToday={d === todayLocalDate}
                          onSet={onSet}
                          busy={busyId === instance.id}
                          open={openId === instance.id}
                          onOpen={(want) => setOpenId(want ? instance.id : null)}
                        />
                      ) : (
                        /* No job of this kind on this day -- a gap in the
                           schedule, not a job that went undone. It has to
                           read as nothing at all. */
                        <span key={n} aria-label="not scheduled" style={{
                          display: "block", width: 26, height: 26,
                          lineHeight: "26px", color: "var(--ink-3)",
                          opacity: 0.35, fontSize: 13 }}>
                          ·
                        </span>
                      ),
                    )}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const LOOK: Record<CellState, { bg: string; border: string; ink: string; mark: string }> = {
  done: { bg: "var(--save)", border: "var(--save-deep)", ink: "#fff", mark: "✓" },
  // Claimed is a promise, not a fact. It reads as done-but-outlined so a child
  // sees their tick landed while a parent can still tell what needs looking at.
  claimed: { bg: "var(--save-soft)", border: "var(--save)", ink: "var(--save-deep)", mark: "✓" },
  missed: { bg: "transparent", border: "var(--bad)", ink: "var(--bad)", mark: "×" },
  excused: { bg: "transparent", border: "var(--ink-3)", ink: "var(--ink-3)", mark: "–" },
  open: { bg: "transparent", border: "var(--paper-edge)", ink: "var(--ink-3)", mark: "" },
};

function Cell({
  instance,
  isToday,
  onSet,
  busy,
  open,
  onOpen,
}: {
  instance: ChoreInstance;
  isToday: boolean;
  onSet?: (instance: ChoreInstance, next: SetState) => void;
  busy: boolean;
  open?: boolean;
  onOpen?: (want: boolean) => void;
}) {
  const state = stateOf(instance);
  const look = LOOK[state];
  const day = instance.dueLocalDate.split("#")[0];
  const label = `${instance.name}, ${day}: ${state}`;
  const button = useRef<HTMLButtonElement>(null);

  const box = {
    width: 26,
    height: 26,
    borderRadius: 8,
    background: look.bg,
    border: `2px solid ${look.border}`,
    color: look.ink,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1,
    display: "grid",
    placeItems: "center",
    opacity: busy ? 0.5 : 1,
    // Today's column is where the eye should land first.
    boxShadow: isToday ? "0 0 0 2px var(--paper), 0 0 0 3.5px var(--ink-3)" : "none",
  } as const;

  if (!onSet) return <span role="img" aria-label={label} style={box}>{look.mark}</span>;

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={!!open}
        disabled={busy}
        onClick={() => onOpen?.(!open)}
        style={{
          ...box, padding: 0, minHeight: 0, cursor: "pointer",
          // The open square keeps a ring so it is obvious which day the menu
          // is about once the menu covers its neighbours.
          outline: open ? "2px solid var(--ink)" : "none",
          outlineOffset: 2,
        }}
      >
        {look.mark}
      </button>
      {open && (
        <StateMenu
          anchor={button.current}
          title={`${instance.name} \u00b7 ${dayLabel(day)}`}
          current={state}
          onPick={(next) => {
            onOpen?.(false);
            onSet(instance, next);
          }}
          onClose={() => {
            onOpen?.(false);
            button.current?.focus();
          }}
        />
      )}
    </>
  );
}

/* The three things a grown-up can say about one square.
 *
 * A menu rather than a cycling tap. There is no tap order that keeps both of
 * the moves a parent actually makes -- putting a missed day right, and undoing
 * a tick -- to one tap while still reaching "excused"; something has to give,
 * and quietly changing what the second tap means on a screen about money is
 * the wrong thing to give. Saying it in words also lets the menu explain what
 * "excused" does, which a third square colour never could.
 *
 * Positioned fixed off the button's own rect: the grid scrolls sideways inside
 * overflow-x, and anything absolutely placed within it gets clipped at the
 * edge -- which is exactly where Saturday lives.
 */
function StateMenu({
  anchor,
  title,
  current,
  onPick,
  onClose,
}: {
  anchor: HTMLElement | null;
  title: string;
  current: CellState;
  onPick: (next: SetState) => void;
  onClose: () => void;
}) {
  const menu = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!anchor) return;

    function place() {
      const a = anchor!.getBoundingClientRect();
      const width = 208;
      const height = menu.current?.offsetHeight ?? 150;
      // Flip above when there is no room below, and keep the whole card on
      // screen horizontally -- the first and last columns are near the edges.
      const below = a.bottom + 6;
      const top = below + height > window.innerHeight - 8
        ? Math.max(8, a.top - 6 - height)
        : below;
      const left = Math.min(
        Math.max(8, a.left + a.width / 2 - width / 2),
        window.innerWidth - width - 8,
      );
      setAt({ top, left });
    }

    place();
    const away = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node) && !anchor!.contains(e.target as Node)) {
        onClose();
      }
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // Scrolling or resizing moves the square out from under a fixed menu, so
    // close rather than chase it.
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, [anchor, onClose]);

  return (
    <div
      ref={menu}
      role="menu"
      aria-label={title}
      style={{
        position: "fixed",
        top: at?.top ?? -9999,
        left: at?.left ?? -9999,
        width: 208,
        zIndex: 50,
        // --raised, not --paper: the grid sits on a card that is ALREADY
        // --paper, so a menu in the same colour reads as a flat patch of the
        // page no matter how much shadow is under it. The ring and the lift
        // then say which way is up.
        background: "var(--raised)",
        border: "1px solid var(--paper-edge)",
        borderRadius: 12,
        boxShadow: "var(--lift-2), 0 0 0 1px rgba(0,0,0,0.10)",
        padding: 6,
        textAlign: "left",
      }}
    >
      <p style={{ margin: "4px 8px 6px", fontSize: 11, fontWeight: 700,
                  letterSpacing: "0.04em", textTransform: "uppercase",
                  color: "var(--ink-3)", overflow: "hidden",
                  textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {title}
      </p>
      {CHOICES.map((choice) => {
        const on = MATCHES[choice.state](current);
        return (
          <button
            key={choice.state}
            type="button"
            role="menuitemradio"
            aria-checked={on}
            autoFocus={on}
            onClick={() => onPick(choice.state)}
            style={{
              display: "block", width: "100%", textAlign: "left",
              background: on ? "var(--paper-edge)" : "transparent",
              border: "none", borderRadius: 8, padding: "8px 8px",
              minHeight: 44, cursor: "pointer", color: "var(--ink)",
            }}
          >
            <span style={{ fontSize: 13.5, fontWeight: on ? 700 : 500 }}>
              {choice.label}
            </span>
            <span style={{ display: "block", fontSize: 11.5, color: "var(--ink-3)" }}>
              {choice.hint}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* Which choice is already true of a square. Not a straight equality: several
   cell states answer to the same choice. A claimed job is the child saying
   done and nobody having checked, so "Done" is not yet true of it -- but
   neither is anything else, which is why nothing shows as chosen there. */
export const MATCHES: Record<SetState, (state: CellState) => boolean> = {
  done: (state) => state === "done",
  // MISSED is what the week concluded about an untouched job, and OPEN is
  // untouched-so-far. "Not done" is the same statement about both.
  not_done: (state) => state === "missed" || state === "open",
  excused: (state) => state === "excused",
};
